import { Box } from '@mui/material';
import { Component, PropsWithChildren, ReactNode } from 'react';
import { palette } from '../../../../config/theme/theme';
import { SandboxLink } from '../components/SandboxLink';

export type BasePrintControllerProps = PropsWithChildren<{
  onRendered: () => void;
  onError: (e: unknown) => void;
}>;

export type BasePrintControllerState = {};

type ExportRenderOptions = {
  header?: boolean;
  footer?: boolean;
};

const DEFAULT_RENDER_OPTIONS: ExportRenderOptions = {
  header: true,
  footer: true,
};

/**
 * Parent controller for React printable templates.
 *
 * Any layout that needs to be printed should extend `ExportController`,
 * load their data and render their layout through `super.renderBase`.
 *
 * When data fetch is complete, controllers must call `onRendered`.
 * If an error occurs, controllers must call `onError`.
 */
export class BasePrintController<P extends BasePrintControllerProps, S extends BasePrintControllerState> extends Component<P, S> {
  private rendered: boolean = false;

  /**
   * This method must be called when the data has been fetched
   * and the template has been rendered to start the print process.
   */
  onRendered() {
    this.rendered = true;
    this.forceUpdate(() => {
      this.forceTableHeights();
      this.props.onRendered();
    });
  }

  /**
   * This method must be called upon errors during the data fetch.
   * `ExportController` is rendered in a `iframe` and does not extend `BaseController`,
   * therefore it has no access to the error notification system through
   * the snackbar displayer.
   * Errors must be propagated and handled from where this controller is used.
   * @param e the error
   */
  onError(e: unknown) {
    this.props.onError(e);
  }

  /**
   * This method must be called to render the layout of a specific export.
   * The layout will be wrapped with the static header and footer common
   * to all exports.
   * @param children the additional layout to be rendered
   * @param options additional options applied to the rendered template
   */
  renderBase(children: ReactNode, options: ExportRenderOptions = DEFAULT_RENDER_OPTIONS) {
    if (!this.rendered) {
      return <></>;
    }

    const { header, footer } = { ...DEFAULT_RENDER_OPTIONS, ...options };
    return (
      <Box
        sx={{
          py: 2,
          px: 4,
          background: `
            url("/assets/summary-background.svg") right top / auto 128px no-repeat,
            linear-gradient(250.99deg, rgb(188, 219, 255) -20.52%, rgb(207, 234, 255) 109.18%) left top / auto 128px no-repeat,
            ${palette.screenBackground.main} left top / 100% 100%
          `,
        }}
      >
        {header && (
          <Box sx={{ mb: 2 }}>
            <SandboxLink />
          </Box>
        )}
        <Box>{children}</Box>
        {footer && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <SandboxLink />
          </Box>
        )}
      </Box>
    );
  }

  render() {
    return this.renderBase(this.props.children);
  }

  private forceTableHeights() {
    // When the component mounts, force the CSS style of each cell to the actual height of their content.
    // Tables marked by the `data-print` attribute need the content of their cells to entirely fill the cell,
    // and the nested `height: 100%` styles only work correctly across browsers if the height of an ancestor is hardcoded.
    const cells = document.querySelectorAll('[data-print="table-layout"] td') as NodeListOf<HTMLTableCellElement>;
    for (const cell of cells) {
      cell.style.height = `${cell.scrollHeight}px`;
    }
  }
}
